// Angular imports
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

// 3rd party imports
import { classToPlain, plainToClass } from 'class-transformer';

// RxJs imports
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

// service imports
import { MPCacheRegistrationService } from '../../_services/shared-services/mp-cache-registration-service';
import { IWorkflowPaymentRequestService } from './dynamic-workflow-payment-request.service';

// Model imports
import { StatusMessageResultDto } from '../../_models/status-message-result-dto';
import { WFParametersDto } from '../../shared-workflow-module/_models/wf-parameter-dto';
import { WFESpecialHandlingResultDto } from '../../shared-workflow-module/_models/wfe-special-handling-result-dto';
import { SimplePaymentRequestEditorResultDto } from '../_models/simple-payment-request-editor-result-dto';
import { PaymentRequestEditorResultDto } from '../_models/payment-request-editor-result-dto';
import { PaymentRequestCodingEditorParamterDto } from '../_models/payment-request-coding-editor-parameter-dto';
import { PaymentRequestCodingEditorResultDto } from '../_models/payment-request-coding-editor-result-dto';
import { PaymentRequestPayeeInfoResultDto } from '../_models/payment-request-payee-info-result-dto';
import { PaymentRequestPayeeInfoParameterDto } from '../_models/payment-request-payee-info-parameter-dto';
import { SimplePaymentRequestRawDto } from '../_models/simple-payment-request-raw-dto';
import { AddPaymentRequestParameterDto } from '../_models/add-payment-request-parameter-dto';
import { SubCategoryByGlAccountParametersDto } from '../../cirabooks/shared-cirabooks-module/_models/common/subcategory-by-gl-account-parametere-dto';
import { GLSubcategorySelectorResultDto } from '../../cirabooks/shared-cirabooks-module/_models/common/gl-account/glsubcategory-selector-result-dto';
import { VendorTemplateParameterDto } from '../../shared-workflow-module/_models/vendor-template-parameter-dto';
import { VendorTemplateResultDto } from '../../shared-workflow-module/_models/vendor-template-result-dto';
import { VendorAccountTemplateParameterDto } from '../../shared-workflow-module/_models/vendor-account-template-parameter-dto';
import { VendorAccountTemplateResultDto } from '../../shared-workflow-module/_models/vendor-account-template-result-dto';
import { WFEVendorResultDto } from '../../shared-workflow-module/_models/wfe-vendor-result-dto';
import { WFEOwnerResultDto } from '../../shared-workflow-module/_models/wfe-owner-result-dto';

// constants imports
import { CacheDuration } from '../../_constants/cache-duration';
import { URLConstants } from '../../_constants/url-constants';

@Injectable()
export class WorkflowPaymentRequestService implements IWorkflowPaymentRequestService {
    baseApiUrl: string = null;
    apiUrl: string = null;

    constructor(
        private readonly httpClient: HttpClient,
        private readonly cacheRegistrationService: MPCacheRegistrationService
    ) {}

    getSimpleEditor(): Observable<SimplePaymentRequestEditorResultDto> {
        const url = `${URLConstants.ccWorkflowViewerBaseApiUrl}submit/simple/editor`;

        return this.httpClient
            .get(url)
            .pipe(map((response: any) => plainToClass(SimplePaymentRequestEditorResultDto, response)));
    }

    getSpecialHandlings(baseParams: WFParametersDto): Observable<WFESpecialHandlingResultDto[]> {
        const url = `${URLConstants.ccWorkflowViewerBaseApiUrl}specialhandlings${baseParams.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, baseParams.clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.tenMinute);
        const options = { headers };

        return this.httpClient
            .get(url, options)
            .pipe(map((response: any[]) => plainToClass(WFESpecialHandlingResultDto, response)));
    }

    submitInvoice(baseParams: SimplePaymentRequestRawDto): Observable<StatusMessageResultDto> {
        return this.httpClient
            .post<any>(`${URLConstants.ccWorkflowViewerBaseApiUrl}submit/simple`, classToPlain(baseParams))
            .pipe(map((response: any) => plainToClass(StatusMessageResultDto, response)));
    }

    getAdvanceEditor(baseParams: WFParametersDto): Observable<PaymentRequestEditorResultDto> {
        const url = `${URLConstants.ccWorkflowViewerBaseApiUrl}submit/advance/editor${baseParams.getQueryString()}`;

        return this.httpClient
            .get(url)
            .pipe(map((response: any) => plainToClass(PaymentRequestEditorResultDto, response)));
    }

    getAdvanceCodingEditor(
        baseParams: PaymentRequestCodingEditorParamterDto
    ): Observable<PaymentRequestCodingEditorResultDto> {
        const url = `${
            URLConstants.ccWorkflowViewerBaseApiUrl
        }submit/advance/coding/editor${baseParams.getQueryString()}`;

        return this.httpClient
            .get(url)
            .pipe(map((response: any) => plainToClass(PaymentRequestCodingEditorResultDto, response)));
    }

    getPayeeInfo(baseParams: PaymentRequestPayeeInfoParameterDto): Observable<PaymentRequestPayeeInfoResultDto> {
        const url = `${URLConstants.ccWorkflowViewerBaseApiUrl}submit/advance/payeeinfo${baseParams.getQueryString()}`;

        return this.httpClient
            .get(url)
            .pipe(map((response: any) => plainToClass(PaymentRequestPayeeInfoResultDto, response)));
    }

    getSubCategories(baseParams: SubCategoryByGlAccountParametersDto): Observable<GLSubcategorySelectorResultDto[]> {
        const url = `${
            URLConstants.ccWorkflowViewerBaseApiUrl
        }submit/advance/glsubcategory${baseParams.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, baseParams.clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.tenMinute);
        const options = { headers };

        return this.httpClient
            .get(url, options)
            .pipe(map((response: any[]) => plainToClass(GLSubcategorySelectorResultDto, response)));
    }

    submitAdvancePayment(baseParams: AddPaymentRequestParameterDto): Observable<StatusMessageResultDto> {
        return this.httpClient
            .post(`${URLConstants.ccWorkflowViewerBaseApiUrl}submit/advance`, classToPlain(baseParams))
            .pipe(map((responseData: any) => plainToClass(StatusMessageResultDto, responseData)));
    }

    getVendorLineTemplate(baseParams: VendorTemplateParameterDto): Observable<VendorTemplateResultDto> {
        const url = `${URLConstants.ccWorkflowViewerBaseApiUrl}vendor/templates${baseParams.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, baseParams.clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };

        return this.httpClient
            .get(url, options)
            .pipe(map((response: any) => plainToClass(VendorTemplateResultDto, response)));
    }

    getVendorAccountLineTemplate(
        baseParams: VendorAccountTemplateParameterDto
    ): Observable<VendorAccountTemplateResultDto> {
        const url = `${URLConstants.ccWorkflowViewerBaseApiUrl}vendor/account/templates${baseParams.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, baseParams.clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };

        return this.httpClient
            .get(url, options)
            .pipe(map((response: any) => plainToClass(VendorAccountTemplateResultDto, response)));
    }

    getVendors(baseParam: WFParametersDto): Observable<WFEVendorResultDto[]> {
        const url = `${URLConstants.ccWorkflowViewerBaseApiUrl}vendors${baseParam.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, baseParam.clearCache);

        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };

        return this.httpClient
            .get<WFEVendorResultDto[]>(url, options)
            .pipe(map((response) => plainToClass(WFEVendorResultDto, response)));
    }

    getOwners(baseParam: WFParametersDto): Observable<WFEOwnerResultDto[]> {
        const url = `${URLConstants.ccWorkflowViewerBaseApiUrl}owners${baseParam.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, baseParam.clearCache);

        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };

        return this.httpClient
            .get<WFEOwnerResultDto[]>(url, options)
            .pipe(map((response) => plainToClass(WFEOwnerResultDto, response)));
    }
}
